import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'

import { makeStyles, useTheme } from '@mui/styles'
import PropTypes from 'prop-types'
import { merge, pick } from 'lodash'

import { AgGridReact } from 'ag-grid-react'
import { ClientSideRowModelModule, InfiniteRowModelModule } from 'ag-grid-community'
import lang from '@/lang'
import { Checkbox } from '@/modules/base/components/inputs'
import { formatDate } from '@/modules/_helpers/formatDate'
import { mimeTypeToReadable } from '@/utils/format-mime-type'
import { fileOriginToDisplayName } from '@/modules/file/utils'
import { useDispatch, useSelector } from 'react-redux'
import CascadingContextMenu from '@/modules/base/components/cascading-context-menu/cascading-context-menu'
import useCurrentModuleContext from '@/modules/_helpers/accessCheck/useCurrentModuleContext'

import IconFavorite from '@assets/images/icons/favorite.svg'
import IconNoFavorite from '@assets/images/icons/no_favorite.svg'

import IconDefault from '@assets/images/icons/mimeTypes/default.svg'
import IconDoc from '@assets/images/icons/mimeTypes/doc.svg'
import IconDocx from '@assets/images/icons/mimeTypes/docx.svg'
import IconJpeg from '@assets/images/icons/mimeTypes/jpeg.svg'
import IconJpg from '@assets/images/icons/mimeTypes/jpg.svg'
import IconJson from '@assets/images/icons/mimeTypes/json.svg'
import IconMp3 from '@assets/images/icons/mimeTypes/mp3.svg'
import IconPdf from '@assets/images/icons/mimeTypes/pdf.svg'
import IconPpt from '@assets/images/icons/mimeTypes/ppt.svg'
import IconPptx from '@assets/images/icons/mimeTypes/pptx.svg'
import IconPng from '@assets/images/icons/mimeTypes/png.svg'
import IconXls from '@assets/images/icons/mimeTypes/xls.svg'
import IconXlsx from '@assets/images/icons/mimeTypes/xlsx.svg'
import IconXml from '@assets/images/icons/mimeTypes/xml.svg'
import IconZip from '@assets/images/icons/mimeTypes/zip.svg'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'

import PlaceHolder from './placeholder'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import customDecodeUri from '@/utils/customDecodeUri'
// FLAG:useDocuAi — child-faced label for rows whose search match came from a child.
import { getMatchedChildLabel } from '@/modules/file/components/matched-child-info'
import Tooltip from '@mui/material/Tooltip'
import orgamax from 'ampersand-app'
import { FILE_LIST_FILTER, FILE_TYPE, experimentalFeatures } from '@/modules/base/util/constants'
import { ROW_SELECTION_MULTIPLE, ROW_SELECTION_SINGLE } from '@/modules/base/components/list/list-v2'
import {
  revokeSelection,
  setItemSelection,
  createNewItemState,
  agGridUpdated,
} from '@/redux/ducks/document/list'
import {
  setDocumentsFavorite,
  fetchListAction,
} from '@/redux/ducks/lists/requests/documentList'
import { LIST_TYPES, QUERY_TYPES, setShallRefresh } from '@/redux/ducks/lists'
import store, { useAppSelector } from '@/redux/store'
import { splitFilename } from '@/modules/_helpers/splitFilename'
// FLAG:useDocuAi — list-view analysis-state cell. Additive: only ever reached when the gated
// `invoiceAnalysisState` column is present (flag-ON), so flag-OFF lists are unchanged.
import InvoiceAnalysisStatusCell from '@/modules/document/components/invoice-analysis-status-cell'
import { getInvoiceAnalysisLabel, getInvoiceAnalysisState } from '@/modules/document/utils/invoice-analysis-state'

const { DOCUMENTS_LIST_TYPE } = LIST_TYPES
const useStyles = makeStyles((theme) => ({
  tagsCell: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  tagCircle: {
    backgroundColor: 'white',
    borderRadius: '50%',
    border: theme.border,
    height: '1em',
    width: '1em',
    margin: 1,
    zIndex: 1,
  },
  fileIconHeader: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    color: '#19233c', // no idea why theme.black doesn't work
    fontSize: 14,
  },
  fileIcon: {
    height: 20,
    width: 16,
  },
  svgWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  favoriteCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '92%',
    '&.isLoading': {
      pointerEvents: 'none',
    },
  },
  revokeSelection: {
    display: 'none',
    '&.isSelected': {
      display: 'flex',
    },
  },
  checkboxWrapper: {
    right: 15,
    top: 16,
    position: 'absolute',
    zIndex: 1,
    height: checkboxWidth,
    width: checkboxWidth,
  },
  checkbox: {
    transform: `scale(${checkboxWidth / originalCheckboxWidth})`,
  },
  item: {
    position: 'relative',
  },
  root: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    '& .hover': {
      backgroundColor: 'var(--ag-row-hover-color, #e9edf9)',
    },
    '& .ag-header-cell': {
      alignItems: 'center',
    },
    '& .ag-center-cols-container': {
      minWidth: '100% !important',
    },
  },
  unseenDocumentCell: {
    marginTop: 20,
  },
  unseenRow: {
    backgroundColor: `${theme.colorPrimaryButtonBackground}0d`,
  },
}),
)

const FavoriteCell = ({ data, classes }) => {
  const dispatch = useDispatch()

  const toggle = async() => {
    dispatch(setDocumentsFavorite([data], !data.isFavorite))
  }

  const isLoading = data.updatingFields?.includes('isFavorite')

  return (
    <div onClick={toggle} className={clsx(classes.favoriteCell, { isLoading })}>
      <SVGIcon width="16px" svg={data.isFavorite ? IconFavorite : IconNoFavorite} />
    </div>
  )
}

const UnseenDocumentCell = ({ data, classes }) => {
  const theme = useTheme()
  return (
    <div className={clsx(classes.unseenDocumentCell)}>
      {data.unseen && data.type === FILE_TYPE.DOCUMENT ? <div className={classes.tagCircle} style={{ backgroundColor: theme.mobileNav.headerButton }} /> : null}
    </div>
  )
}

const checkboxWidth = 18
const originalCheckboxWidth = 22

const onWindowResize = (grid) => {
  return () => {
    grid.api.sizeColumnsToFit()
  }
}

const agColumnPropNames = [
  'field',
  'headerName',
  'suppressAutoSize',
  'resizable',
  'suppressMovable',
  'filter',
  'width',
  'minWidth',
  'maxWidth',
  'suppressMenu',
  'lockPosition',
  'cellRenderer',
  'headerComponent',
  'valueFormatter',
  'hide',
]

const CUSTOM_CLASS_FIRST = 'ag-custom-first'

const processCellForClipboard = (params) => {
  const { value, column: { colId }, node: { data } } = params
  switch (colId) {
    case 'recordDate':
      return formatDate(value)

    case 'isAssigned':
      return value ? lang.generics.yes : lang.generics.no

    case 'origin':
      return fileOriginToDisplayName(value)

    case 'mimetype':
      return mimeTypeToReadable(value)

    // FLAG:useDocuAi — copy the human-readable analysis-state label (column present flag-ON only).
    case 'invoiceAnalysisState':
      return getInvoiceAnalysisLabel(getInvoiceAnalysisState(data)) || '-'

    default:
      return data[colId] || value
  }
}

export const FileFlatList = (props) => {
  const [grid, setGrid] = useState(null)
  const dispatch = useDispatch()
  const classes = useStyles(props)
  // FLAG:useDocuAi — selects the non-destructive cache refresh below (see the shallRefresh effect).
  const useDocuAiEnabled = orgamax.user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  const {
    onSelectFile,
    className,
    multiSelect = true,
    hideMenuTabHeader,
    disableRightClickContextMenu,
    columns,
    fetchOptions = {},
  } = props

  const { pagination } = useAppSelector(state => state.document.list)

  const { orderBy, sortOrder, totalCount } = pagination

  const {
    updateAgGrid,
    updatedData,
    options,
    getContextMenu,
  } = useSelector(state => state.document.list)

  const {
    shallRefresh,
  } = useSelector(state => state.lists[DOCUMENTS_LIST_TYPE])

  const { rights } = useCurrentModuleContext()
  const selectedItems = useSelector(state => state.document.list.selectedItems)
  const handleCellContextMenu = useCallback((params) => {
    const { data, event } = params
    if (!data) return
    event.preventDefault()
    if (!selectedItems.some(si => si.id === data.id)) {
      dispatch(revokeSelection())
    }
  }, [dispatch, selectedItems])

  const getColumnClass = (params) => {
    const columnApi = params.columnApi
    const columns = columnApi.getAllGridColumns()
    const classes = []

    if (columns[0].colId === params.colDef.field) {
      classes.push(CUSTOM_CLASS_FIRST)
    }

    return classes
  }

  const columnDefs = columns.map((item) => (
    pick({
      ...item,
      cellClass: getColumnClass,
      suppressMenu: true,
      lockPosition: true,
      field: item.name,
      headerName: item.label,
      hide: !item.active,
    }, agColumnPropNames)
  ))

  const onChangeSelection = (file, selected, selectedItems, api, modifiers) => {
    dispatch(setItemSelection(file, selected, modifiers))
    api.forEachNode((node) => { // this guarantees that the cells context menus are rebuild
      const { data } = node
      if (data && selectedItems.find(({ id }) => id === data.id)) {
        node.setData(data)
      }
    })
  }

  const onRevokeSelection = () => {
    dispatch(revokeSelection())
  }

  const components = {
    menuCell: ({ valueFormatted, node: { data } }) => {
      if (!data) return ''
      const menuItems = getContextMenu(rights, data).filter(item => item.showInContextMenu !== false)
      return (
        <CascadingContextMenu closeOnClick hoverParentClass='ag-row' disabled={disableRightClickContextMenu} menuItems={menuItems}>
          <div className={classes.item} onClick={() => handleRowClicked(data)}>{customDecodeUri(valueFormatted)}</div>
        </CascadingContextMenu>
      )
    },
    previewCell: ({ valueFormatted, node: { data } }) => {
      if (!data) return ''
      const menuItems = getContextMenu(rights, data).filter(item => item.showInContextMenu !== false)
      const { mimetype, thumbnailUrl } = data
      // FLAG:useDocuAi — search match came from a child document: show the CHILD's
      // name + thumbnail preview on the parent row (matchedChild is only ever
      // attached server-side for flagged tenants). Belt-and-braces client gate like
      // thumbnail-list-item (F16.3): after a flag rollback a cached/SSR row may still
      // carry the field, and the two views of the same grid must not disagree.
      const matchedChild = useDocuAiEnabled ? data.matchedChild : undefined
      const cellLabel = matchedChild
        ? `${getMatchedChildLabel(matchedChild)} (aus: ${customDecodeUri(valueFormatted)})`
        : customDecodeUri(valueFormatted)
      const width = thumbnailUrl || data.minioId || matchedChild?.thumbnailUrl ? 320 : 90
      return (
        <CascadingContextMenu hoverParentClass='ag-row' disabled={disableRightClickContextMenu} menuItems={menuItems}>
          <RichTooltip content={<PlaceHolder name={matchedChild ? matchedChild.childName : data.name} mimetype={mimetype} size={width}
            minioId={matchedChild ? matchedChild.childMinioId : data.minioId} thumbnailUrl={matchedChild ? matchedChild.thumbnailUrl : thumbnailUrl} />} width={width} offsetX={180}>
            <div className={classes.item} onClick={() => handleRowClicked(data)}>{cellLabel}</div>
          </RichTooltip>
        </CascadingContextMenu>
      )
    },
    selectCell: ({ api, data }) => {
      const { selectedItems } = useSelector(state => state.document.list)
      if (!data) return ''
      const isSelected = !!selectedItems.find(({ id }) => id === data.id)
      api.sizeColumnsToFit()
      return (
        <div className={classes.checkboxWrapper}>
          <Checkbox customVisualClass={classes.checkbox} useFormik={false} name='yoda' checked={isSelected} onChange={(selected, _name, modifiers) => {
            onChangeSelection(data, selected, selectedItems, api, modifiers)
          }} />
        </div>
      )
    },
    selectCellHeader: () => {
      const { selectedItems } = useSelector(state => state.document.list)
      const isSelected = !!selectedItems.length

      return (
        <div className={classes.checkboxWrapper}>
          <Checkbox customVisualClass={clsx(classes.checkbox, classes.revokeSelection, { isSelected })} useFormik={false} name='obi-wan' checked={isSelected} onChange={onRevokeSelection} />
        </div>
      )
    },
    fileIconCell: ({ data }) => {
      const menuItems = !data ? [] : getContextMenu(rights, data).filter(item => item.showInContextMenu !== false)

      const getIcon = () => {
        switch (data?.mimetype) {
          case 'image/png':
            return IconPng
          case 'image/jpg':
            return IconJpeg
          case 'image/jpeg':
            return IconJpg
          case 'application/pdf':
            return IconPdf
          default:
            if (!data?.name) {
              return IconDefault
            }
            switch (splitFilename(data.name).suffix) {
              case '.xls':
              case '.csv':
                return IconXls
              case 'xlsx':
                return IconXlsx
              case '.doc':
                return IconDoc
              case '.docx':
                return IconDocx
              case '.ppt':
                return IconPpt
              case '.pptx':
                return IconPptx
              case '.json':
                return IconJson
              case '.mp3':
                return IconMp3
              case '.xml':
                return IconXml
              case '.zip':
                return IconZip
            }
        }

        return IconDefault
      }

      const icon = getIcon()

      return (
        <CascadingContextMenu hoverParentClass='ag-row' height={'100%'} disabled={disableRightClickContextMenu} menuItems={menuItems}>
          {icon ? <div className={classes.svgWrapper}><SVGIcon className={classes.fileIcon} svg={icon} /></div> : mimeTypeToReadable(data?.mimetype)}
        </CascadingContextMenu>
      )
    },
    fileIconHeader: () => {
      return (
        <div className={classes.fileIconHeader}>
          Typ
        </div>
      )
    },
    favoriteCell: ({ data }) => {
      if (!data) return null
      return <FavoriteCell data={data} classes={classes} />
    },
    favoriteCellHeader: () => null,
    unseenDocumentCell: ({ data }) => {
      if (!data) return null
      return <UnseenDocumentCell data={data} classes={classes} />
    },
    unseenDocumentHeader: () => null,
    // FLAG:useDocuAi — renderer for the gated `invoiceAnalysisState` column. Inert flag-OFF: the
    // column is only injected (and thus only resolves this renderer) on the useDocuAi path.
    invoiceAnalysisStateCell: ({ data, value }) => {
      if (!data) return null
      return <InvoiceAnalysisStatusCell data={data} value={value} onClick={handleRowClicked} liveDerived />
    },
    tagsCell: ({ data }) => {
      if (!data || !data.tags.length) return ''

      return (
        <div className={classes.tagsCell} onClick={() => handleRowClicked(data)}>{data.tags
          .filter(({ isSystem }) => !isSystem)
          .map((tag) =>
            (<Tooltip key={tag?.id} title={tag?.label}>
              <div className={classes.tagCircle} style={{ backgroundColor: tag?.backgroundColor }} />
            </Tooltip>),
          )}</div>
      )
    },
  }

  const onModelUpdated = () => {
    grid?.api && grid.api.sizeColumnsToFit()
  }

  const onGridColumnsChanged = ({ api }) => {
    if (api) {
      api.sizeColumnsToFit()
    }
  }

  const onGridReady = (gridInstance) => {
    setGrid(gridInstance)
    gridInstance.api.setDatasource(dataSource)
    dispatch(setShallRefresh(DOCUMENTS_LIST_TYPE, true))
  }

  const handleRowClicked = (file) => {
    onSelectFile(file)
  }

  useEffect(() => {
    setTimeout(() => {
      grid?.api?.sizeColumnsToFit()
    })
  }, [columns])

  useEffect(() => {
    const handleWindowResize = onWindowResize(grid)
    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [grid])

  useEffect(() => {
    dispatch(setShallRefresh(DOCUMENTS_LIST_TYPE, true))
  }, [orderBy, sortOrder, options])

  useEffect(() => {
    updateAgGrid && dispatch(agGridUpdated())
    updateAgGrid && dispatch(setShallRefresh(DOCUMENTS_LIST_TYPE, false))
  }, [updateAgGrid])

  useEffect(() => {
    if (updatedData) {
      updatedData.forEach((data) => {
        const rowNode = grid?.api.getRowNode(data.id)
        rowNode?.setData(data)
      })
      updateAgGrid && dispatch(agGridUpdated())
      if (options?.filter === FILE_LIST_FILTER.FAVORITE && updatedData.find(({ isFavorite }) => !isFavorite)) {
        dispatch(setShallRefresh(DOCUMENTS_LIST_TYPE, true))
      }
    }
  }, [updatedData])

  useEffect(() => {
    if (!grid || !shallRefresh) return
    // FLAG:useDocuAi — refreshInfiniteCache reloads the loaded blocks IN PLACE (keeps the current
    // rows visible until the new data arrives), so the live per-upload refresh no longer wipes the
    // grid to empty and flickers. flag-OFF keeps the verbatim development purge (clear + refetch).
    if (useDocuAiEnabled) {
      grid.api.refreshInfiniteCache()
    } else {
      grid.api.purgeInfiniteCache()
    }
  }, [shallRefresh])

  const getRowFunction = (params) => {
    const { startRow, successCallback } = params
    const shallRefresh = store.getState().lists[DOCUMENTS_LIST_TYPE].shallRefresh
    const queryType = shallRefresh ? QUERY_TYPES.ALL : QUERY_TYPES.DATA
    if (shallRefresh) {
      dispatch(setShallRefresh(DOCUMENTS_LIST_TYPE, false))
    }
    const shouldClear = startRow === 0
    dispatch(fetchListAction({ queryType, offset: startRow, clear: shouldClear, ...fetchOptions }, ({ data, meta: { count, dataByInclude = [] } }) => {
      if (shouldClear) {
        const temp = createNewItemState({ items: dataByInclude }, data, true)
        successCallback(temp, count)
        return
      }
      successCallback(data, count)
    }))
    return
  }

  const dataSource = {
    rowCount: totalCount,
    startRow: 0,
    endRow: 20,
    getRows: getRowFunction,
  }

  const contextMenuLocaleText = {
    copy: lang.list.copy,
    ctrlC: ' ',
  }

  const getRowClass = ({ data }) => {
    if (data?.unseen && data.type === FILE_TYPE.DOCUMENT) {
      return classes.unseenRow
    }
  }

  return (
    <div className={clsx('ag-theme-material', hideMenuTabHeader && 'hide-menu-tab-header', classes.root, className)}>
      <AgGridReact
        getRowClass={getRowClass}
        modules= {[ClientSideRowModelModule, InfiniteRowModelModule]}
        columnDefs={columnDefs}
        rowModelType='infinite'
        enableColResize={false}
        enableSorting={false}
        suppressRowDrag={true}
        components={components}
        suppressContextMenu={true}
        onCellContextMenu={handleCellContextMenu}
        maxConcurrentDatasourceRequests="1"
        onGridColumnsChanged={onGridColumnsChanged}
        onGridReady={onGridReady}
        paginationPageSize={20}
        cacheBlockSize={20}
        onModelUpdated={onModelUpdated}
        rowSelection={multiSelect ? ROW_SELECTION_MULTIPLE : ROW_SELECTION_SINGLE}
        pagination={true}
        localeText={merge(lang.agGrid, { noRowsToShow: lang.file.list.emptyStateText }, contextMenuLocaleText)}
        processCellForClipboard={processCellForClipboard}
        getRowId={(params) => {
          return params.data.id
        }}
      />
    </div>
  )
}

FileFlatList.propTypes = {
  onSelectFile: PropTypes.func,
  className: PropTypes.string,
  multiSelect: PropTypes.bool,
  hideMenuTabHeader: PropTypes.bool,
  columns: PropTypes.array.isRequired,
  fetchOptions: PropTypes.object,
}

FileFlatList.defaultProps = {
  onSelectFile: () => { },
}

export default FileFlatList
